// The IMPURE half of the step table: gates that read live app state, and
// actions that poke the app. `tourSteps.js` stays pure data and refers to these
// by key, so the step table remains readable and this file stays the only place
// that knows how the app exposes anything.
//
// GATE CONTRACT. A gate makes a step wait for the user to actually do something
// (find a printer, link Canva) instead of just reading about it — the point of
// the tour is that the app ends up SET UP. Rules:
//  1. `satisfied()` must be cheap and side-effect free. It is polled every
//     GATE_POLL_MS while the step is on screen.
//  2. It must never throw. A gate that throws would lock the step forever;
//     everything here is wrapped.
//  3. Every gated step is SKIPPABLE. A teacher with no printer on the LAN, or
//     no Canva account, must be able to finish the tour.
//  4. Observe the app the way another app would — a rendered <option>, a
//     persisted field. Never reach into component state or a private global.

export const GATE_POLL_MS = 400;

export const TOUR_GATES = {
  // Satisfied once discovery (or a manual IP add) has put at least one
  // printer in the list. Both routes feed the same list, and the Printers box
  // renders its size as data-printer-count, so one probe covers "pressed Find"
  // and "typed an IP".
  printerFound: {
    satisfied: () => {
      try {
        return Number(document.querySelector('[data-printer-count]')?.getAttribute('data-printer-count') || 0) > 0;
      } catch {
        return false;
      }
    },
  },

};

// Actions a step can fire on entry (`before`) or exit (`after`). Window events
// only — App.jsx owns the modal state, and the tour must not learn otherwise.
export const TOUR_ACTIONS = {
  // Print Preview's right-panel boxes are collapsible; a step that points into
  // one opens it first (PrintPreview.jsx listens).
  openPrintersBox: () => window.dispatchEvent(new Event('pse-open-printers-box')),
  openInkBox: () => window.dispatchEvent(new Event('pse-open-ink-box')),
};

export function runTourAction(key) {
  if (!key) return;
  const action = TOUR_ACTIONS[key];
  if (!action) {
    console.warn('[tour] unknown action:', key);
    return;
  }
  try {
    action();
  } catch (error) {
    console.error('[tour] action failed:', key, error);
  }
}

export function isGateSatisfied(key) {
  if (!key) return true;
  const gate = TOUR_GATES[key];
  if (!gate) {
    console.warn('[tour] unknown gate:', key);
    return true; // an unknown gate must never lock the tour
  }
  try {
    return !!gate.satisfied();
  } catch {
    return false;
  }
}
